import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MenuItem, Stack, TextField, Typography, useMediaQuery } from "@mui/material";
import PageContainer from "src/components/container/PageContainer";
import PageHeader from "src/components/shared/PageHeader";
import ResourceList from "src/components/shared/ResourceList";
import PersonAvatar from "src/components/shared/PersonAvatar";
import ActionButton from "src/components/shared/ActionButton";
import { EMPTY, formatDate } from "src/components/shared/format";
import { STATUS } from "src/pages/cases/format";
import { GetRequest, findRequests } from "./actions";
import { CasesNote, PaidChip, StatusChip, caseIdFor } from "./parts";

// "Cases by traveller" (QA nadia-27, rania-15): the same applications as
// Cases, one row per traveller, numbered as on the case (1611-2), each row
// opening its case on that traveller. It keeps what only this list had: the
// date filter and finding one traveller by name or number. There is no
// second numbering and no second screen for an application.
const numberOf = (row) => (row.case_id ? `${row.case_id}-${row.traveller_number || 1}` : `#${row.id}`);
export const caseLink = (row) => `/cases/${row.case_id || row.id}${row.case_id && row.traveller_number > 1 ? `?traveller=${row.traveller_number}` : ""}`;

// What to show. The list opens on the requests waiting for the team: a new
// submission is the next thing to pick up. Drafts are customers still filling
// in the form, so they come last.
const SHOW = [
  { value: "pending", label: "Waiting for us" },
  { value: "under_review", label: STATUS.under_review.label },
  { value: "approved", label: STATUS.approved.label },
  { value: "rejected", label: STATUS.rejected.label },
  { value: "cancelled", label: STATUS.cancelled.label },
  { value: "expired", label: STATUS.expired.label },
  { value: "incomplete", label: "Drafts (not submitted yet)" },
  { value: "all", label: "Everything" },
];
const DEFAULT_SHOW = "pending";
const PERIODS = [
  { value: "none", label: "Any date" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "year", label: "This year" },
];

// Names are optional at sign-up: fall back to the email, then a plain word.
// The partner platform that sent the request (e.g. Safrati Travel), when
// the API names it (partner_name, or partner: { name }).
const partnerOf = (row) => row.partner_name || row.partner?.name || null;
const nameOf = (row) => row.client_name?.trim() || row.client_email || "Customer";
// The traveller's own name from their passport answers (CMS review round 2,
// nadia-08): the row's title, with the account holder under it.
export const travellerOf = (row) => row.traveller_name?.trim() || null;
const titleOf = (row) => travellerOf(row) || nameOf(row);
// Product title and visa type, once each: many products are titled after
// their type ("E-visa · E-visa").
const visaLine = (row) =>
  [...new Set([row.visa_type_price_title, row.visa_type_name].map((part) => String(part || "").trim()).filter(Boolean))].join(" · ");

const Requests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const show = SHOW.some((s) => s.value === params.get("show")) ? params.get("show") : DEFAULT_SHOW;
  const period = PERIODS.some((p) => p.value === params.get("period")) ? params.get("period") : "none";
  const page = Math.max(Number(params.get("page")) || 0, 0);
  const search = params.get("q") || "";
  const [typed, setTyped] = useState(search);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [state, setState] = useState({ loading: true, failed: false, rows: null, total: 0 });
  const request = useRef(0);
  // Below 400px the two filters stack, each the full width of the page.
  const narrow = useMediaQuery("(max-width:399.95px)");

  const update = useCallback(
    (changes) => {
      const next = new URLSearchParams(params);
      Object.entries(changes).forEach(([key, value]) => {
        const fallback = { show: DEFAULT_SHOW, period: "none", page: 0, q: "" }[key];
        if (value == null || value === fallback) next.delete(key);
        else next.set(key, String(value));
      });
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  // One load per change of filters; a slower earlier answer never overwrites
  // a newer one.
  const load = useCallback(() => {
    const ticket = ++request.current;
    setState((s) => ({ ...s, loading: true, failed: false }));
    const done = (response) => {
      if (ticket !== request.current) return;
      if (response === false) {
        setState((s) => ({ ...s, loading: false, failed: true }));
        return;
      }
      const rows = Array.isArray(response?.data) ? response.data : [];
      setState({ loading: false, failed: false, rows, total: search ? rows.length : response?.meta?.total ?? rows.length });
    };
    // Search results come oldest first, the way Cases lists them.
    const byDate = (a, b) => new Date(a.request_date || a.created_date) - new Date(b.request_date || b.created_date);
    if (search)
      findRequests(search, dispatch)
        .then((rows) => done({ data: rows.slice().sort(byDate) }))
        .catch(() => done(false));
    // Waiting for us: the longest-waiting first, as in Cases.
    else dispatch(GetRequest({ status: show, categorize_by: period, page, rowsPerPage, sort: show === "pending" ? "oldest" : undefined, callback: done }));
  }, [dispatch, search, show, period, page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  // Search as the person types, after a short pause.
  useEffect(() => {
    if (typed.trim() === search) return undefined;
    const timer = setTimeout(() => update({ q: typed.trim(), page: 0 }), 400);
    return () => clearTimeout(timer);
  }, [typed, search, update]);

  // Search answers every match at once; page through them here.
  const rows = state.rows ? (search ? state.rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage) : state.rows) : [];
  const showLabel = SHOW.find((s) => s.value === show)?.label;
  const filtered = show !== DEFAULT_SHOW || period !== "none";
  // Rows know their case (case_id); an older API's rows look it up.
  const openCase = (row) =>
    row.case_id
      ? navigate(caseLink(row))
      : caseIdFor(row.id, dispatch)
          .catch(() => row.id)
          .then((id) => navigate(`/cases/${id}`));

  // A search looks through every status and date (the subtitle says so), so
  // the filters step aside while it runs rather than show choices it ignores.
  const fieldSx = (minWidth) => ({ minWidth: narrow ? 0 : minWidth, flex: { xs: 1, sm: "none" }, "& .MuiInputBase-root": { minHeight: 40 } });
  const filters = search ? null : (
    <Stack direction={narrow ? "column" : "row"} spacing={1.5}>
      <TextField
        select
        size="small"
        label="Show"
        value={show}
        fullWidth={narrow}
        onChange={(e) => update({ show: e.target.value, page: 0 })}
        sx={fieldSx(190)}
      >
        {SHOW.map((s) => (
          <MenuItem key={s.value} value={s.value}>
            {s.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Requested"
        value={period}
        fullWidth={narrow}
        onChange={(e) => update({ period: e.target.value, page: 0 })}
        sx={fieldSx(140)}
      >
        {PERIODS.map((p) => (
          <MenuItem key={p.value} value={p.value}>
            {p.label}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );

  return (
    <PageContainer title="Cases by traveller" description="Every traveller on every case">
      <PageHeader
        title="Cases by traveller"
        subtitle={
          search
            ? `Searching every status and date by ${/^#?\d+(-\d+)?$/.test(search.trim()) ? "number" : search.includes("@") ? "email" : "traveller and customer name"}, oldest first.`
            : `${showLabel}${period !== "none" ? `, ${PERIODS.find((p) => p.value === period).label.toLowerCase()}` : ""}${show === "pending" ? ", oldest first" : ""}. One row per traveller.`
        }
        sx={{ mb: 2 }}
      />

      <CasesNote>
        The same applications as Cases, one row per traveller, numbered as on the case (1611-2 is the second traveller of case 1611). Each row opens its case.
      </CasesNote>

      <ResourceList
        label="Cases by traveller"
        items={rows}
        getKey={(row) => row.id}
        primaryLabel="Traveller"
        primary={(row) => ({
          title: titleOf(row),
          subtitle: (
            <>
              <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
                {numberOf(row)}
              </Typography>
              {row.nationality ? ` · ${row.nationality}` : ""}
              {/* The account holder, when the title is the traveller's own name. */}
              {travellerOf(row) && travellerOf(row).toLowerCase() !== String(row.client_name || "").trim().toLowerCase() ? (
                <Typography component="span" variant="body2" color="text.secondary" noWrap sx={{ display: "block" }}>
                  Account: {nameOf(row)}
                </Typography>
              ) : null}
              {/* A partner's traveller (no email with us): who sent it. */}
              {partnerOf(row) ? (
                <Typography component="span" variant="body2" color="text.secondary" noWrap sx={{ display: "block" }}>
                  via {partnerOf(row)}
                </Typography>
              ) : null}
              {!partnerOf(row) && row.client_email && row.client_email !== nameOf(row) && !travellerOf(row) && (
                <Typography component="span" variant="body2" color="text.secondary" noWrap sx={{ display: "block" }}>
                  {row.client_email}
                </Typography>
              )}
            </>
          ),
          avatar: <PersonAvatar name={titleOf(row)} src={travellerOf(row) ? undefined : row.client_image} size={40} />,
        })}
        columns={[
          {
            key: "visa",
            label: "Visa",
            render: (row) => (
              <>
                <Typography fontWeight={600}>{row.destination?.trim() || EMPTY}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {visaLine(row) || EMPTY}
                </Typography>
              </>
            ),
          },
          {
            key: "status",
            label: "Status",
            inCard: true,
            render: (row) => (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <StatusChip status={row.status} />
                <PaidChip isPaid={row.is_paid} status={row.status} partner={partnerOf(row) || undefined} />
              </Stack>
            ),
          },
          {
            key: "requested",
            label: "Requested",
            hideBelow: "lg",
            // Always the day itself ("21 Oct 2026"), in a list and in search results alike.
            render: (row) => <span style={{ whiteSpace: "nowrap" }}>{formatDate(row.request_date || row.created_date) || EMPTY}</span>,
          },
        ]}
        onOpen={openCase}
        openHref={(row) => (row.case_id ? caseLink(row) : null)}
        openLabel={(row) => `Open case ${numberOf(row)} for ${titleOf(row)}`}
        search={{
          value: typed,
          onChange: setTyped,
          placeholder: "Traveller or customer name, email or number (1611-2)",
          label: "Search by the traveller’s own name, the customer’s name or email, the case number or the traveller number",
        }}
        filters={filters}
        loading={state.loading}
        error={state.failed}
        onRetry={load}
        empty={{
          title: filtered ? "No traveller matches these filters." : "Nothing is waiting for us.",
          hint: filtered ? undefined : "New submissions appear here. Customers still filling in the form are under Drafts.",
          action: filtered ? <ActionButton onClick={() => update({ show: DEFAULT_SHOW, period: "none", page: 0 })}>Show what is waiting</ActionButton> : undefined,
        }}
        pagination={{
          page,
          rowsPerPage,
          total: state.total,
          onPageChange: (next) => update({ page: next }),
          onRowsPerPageChange: (next) => {
            setRowsPerPage(next);
            update({ page: 0 });
          },
        }}
      />
    </PageContainer>
  );
};

export default Requests;
